import { useEffect, useRef, useState } from "react";
import { Button } from "../ui";
import { postOpenChatSummaryProposal } from "../lib/postmessage";
import { useLabel } from "../lib/labels";
import { recordProposalFunnelClick } from "../api/proposalFunnelClick";
import {
  CLICK_RECORD_WAIT_MS,
  createSubmittedPoller,
  fetchSummaryNudgeSubmitted,
  settleWithin,
  type SubmittedPoller,
} from "../api/summaryNudgeStatus";

interface ChatSummaryNudgeData {
  locale?: string;
  variant?: "scenario" | "reentry" | "exit";
  /** Scenario 카드만. deferral | compare | unsellable. 빈 문자열은 조건 없음. */
  scenarioTrigger?: string;
  /** 로그인 계정 이메일 — 없으면 빈 문자열(게스트). Proposal 모달 prefill. */
  defaultEmail?: string;
  labels?: Record<string, string>;
}

interface Props {
  data: ChatSummaryNudgeData;
  sessionToken: string;
}

/** 카드 식별 — 위젯 iframe URL 의 resultId (카드 1장 = resultId 1개). */
function currentResultId(): string {
  try {
    return new URLSearchParams(window.location.search).get("resultId") || "";
  } catch {
    return "";
  }
}

/**
 * Ask LG Proposal Scenario / Re-Entry 넛지.
 * 마크업 = Host chatsummary-layer.confirm (기획 DS).
 * CTA → ``action.open_chat_summary_proposal`` 에 모달용 ``labels`` 동봉
 * (ConfirmationCard 가 tool/args 를 넘기는 것과 동일 — host 는 payload 만 렌더).
 *
 * 같은 카드 재발송 차단: Submit 은 BrFrame 모달이 하므로 위젯은 결과를 모른다.
 * 클릭 시 resultId 를 서버에 남기고 저장이 끝난 뒤 모달을 연다 (Submit 이 클릭 기록을
 * 앞지르지 않게, 상한 CLICK_RECORD_WAIT_MS). 이후 summary-nudge-status 를 확인해
 * Submit 이 도달하면 CTA 를 비활성화한다. 새 넛지 카드는 resultId 가 달라 영향 없음.
 */
export default function ChatSummaryNudgeCard({ data, sessionToken }: Props) {
  const isExit = (data.variant || "scenario") === "exit";
  const title = useLabel(
    isExit ? "exitTitle" : "scenarioTitle",
    isExit ? "Before you go..." : "Take it with you?",
  );
  const body = useLabel(
    isExit ? "exitBody" : "scenarioBody",
    isExit
      ? "Take your top picks with you! We'll email an Ask LG Proposal highlighting the main products we discussed."
      : "I can email you an Ask LG Proposal highlighting the main products we've focused on so far.",
  );
  const cta = useLabel("proposalCta", "Get My Proposal");

  const resultId = useRef(currentResultId()).current;
  const [submitted, setSubmitted] = useState(false);
  const poller = useRef<SubmittedPoller | null>(null);
  const opening = useRef(false);

  // iframe 재마운트(새로고침) 시 이미 보낸 카드면 처음부터 비활성.
  useEffect(() => {
    let cancelled = false;
    const current = createSubmittedPoller(
      () => fetchSummaryNudgeSubmitted(sessionToken, resultId),
      () => setSubmitted(true),
    );
    poller.current = current;
    void fetchSummaryNudgeSubmitted(sessionToken, resultId).then((done) => {
      if (!cancelled && done) setSubmitted(true);
    });
    return () => {
      cancelled = true;
      current.stop();
      if (poller.current === current) poller.current = null;
    };
  }, [sessionToken, resultId]);

  const handleOpen = async () => {
    if (submitted || opening.current) return;
    opening.current = true;
    try {
      await settleWithin(
        recordProposalFunnelClick(
          sessionToken,
          data.variant || "scenario",
          data.scenarioTrigger,
          resultId,
        ),
        CLICK_RECORD_WAIT_MS,
      );
    } finally {
      opening.current = false;
    }
    postOpenChatSummaryProposal({
      sessionId: sessionToken || null,
      variant: data.variant || "scenario",
      locale: data.locale || null,
      defaultEmail: String(data.defaultEmail || "").trim(),
      labels: data.labels || null,
    });
    if (sessionToken && resultId) poller.current?.start();
  };

  return (
    <div className="ai-gp1--section">
      <div
        className={`chatsummary-layer-container chat-summary-nudge${
          isExit ? " chat-summary-nudge--exit" : " chat-summary-nudge--scenario"
        }`}
      >
        <div className="chatsummary-layer confirm">
          <div className="layer-content">
            <div className="layer-title-wrap">
              <strong className="title">{title}</strong>
              <p className="title-desc">{body}</p>
            </div>
          </div>
          <div className="layer-footer">
            <Button
              className="chat-summary-nudge__btn"
              variant="primary"
              size="sm"
              fullWidth
              disabled={submitted}
              aria-disabled={submitted}
              onClick={handleOpen}
            >
              {cta}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
